import { useRxCollection } from '@/providers/rxdb/rx-hooks';
import type { RxDocument } from 'rxdb';

import {
  useRXDeleteByIds,
  useRxDocument,
  useRxCreateMutation,
  useDuplicateMutation,
  useRxDocuments,
  useRxDocumentsLazy,
  useRxWriter,
  useRxPatchMutation,
  useRxDocumentLazy,
} from '@/hooks/rxdb-base';
import type { MutationOptions } from '@/hooks/rxdb-base';

import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import type { Note } from '@flintmint/core-shared/rxdb';
import type { NoteVariant } from '@/providers/rxdb/constants';
import { appRoutes } from '@/constants/routes';
import { clearTemplateIdFromLinkedNotes } from '@/providers/rxdb/note-handler';
import { generateNodeHash } from '@/utils/helper-utils';

export const DEFAULT_NOTE_NAME = 'Untitled';

export const createBlankEditorContent = () => `<p _hash="${generateNodeHash()}"></p>`;

// create

export const useNoteCreate = (variant: NoteVariant = 'notes') => {
  const { mutate: createMutation, isLoading } = useRxCreateMutation<Note>('notes');

  const createWithDefaults = async (payload: Partial<Note>, mutationOptions?: MutationOptions) => {
    const noteData = {
      content: createBlankEditorContent(),
      name: DEFAULT_NOTE_NAME,
      variant,
      instructions: {},
      blockInstructions: {},
      userSources: {},
      aliases: [],
      tagIds: [],
      blockIds: [],
      ...payload,
    };

    return createMutation(noteData, mutationOptions);
  };

  return { mutate: createWithDefaults, isLoading };
};

export const useNoteDuplicate = () => useDuplicateMutation<Note>('notes');

export const useCreateNoteFromTemplate = () => {
  const collection = useRxCollection<Note>('notes');
  const { mutate: createNote, isLoading } = useRxCreateMutation<Note>('notes');

  const navigateWithSearch = useNavigateWithSearch();

  const createNoteFromTemplate = async (templateId: string, options?: MutationOptions) => {
    const templateDoc = await collection?.findOne(templateId).exec();

    if (!templateDoc) {
      throw new Error(`Template with ID "${templateId}" was not found.`);
    }

    if (templateDoc.variant !== 'templates') {
      throw new Error(`Document with ID "${templateId}" is not a valid template.`);
    }

    const templateData = templateDoc.toJSON() as Note;

    return await createNote(
      {
        ...templateData,
        templateId: templateData.id, // self-ref to the original note
        variant: 'notes',
      },
      {
        onSuccess: (response) => {
          navigateWithSearch(`${appRoutes.library}/${response.id}`);
        },
        ...options,
      }
    );
  };

  return {
    isLoading,
    mutate: createNoteFromTemplate,
  };
};

// read

export const useNote = (id?: string) => useRxDocument<Note>('notes', id);

export const useNotesLazy = () => useRxDocumentsLazy<Note>('notes');

export const useNoteLazy = () => useRxDocumentLazy<Note>('notes');

export const useNotesLinkedToTemplate = (templateId: string) => {
  return useRxDocuments<Note>('notes', { selector: { templateId } });
};

// update

export const useNotePatch = () => {
  const notesCollection = useRxCollection<Note>('notes');
  const { mutate: patchRxDoc, isLoading } = useRxPatchMutation<Note>();

  const patchNote = async (
    rxDoc: RxDocument<Note>,
    payload: Partial<Note>,
    mutationOptions: MutationOptions = {}
  ): Promise<RxDocument<Note>> => {
    const { onSuccess, ...restMutationOptions } = mutationOptions;
    const updatedRxDoc = await patchRxDoc(rxDoc, payload, restMutationOptions);

    if (notesCollection) {
      // If it's now a template and has templateId (was previously note) - clear its templateId
      if (updatedRxDoc.variant === 'templates' && updatedRxDoc.templateId) {
        await updatedRxDoc.incrementalPatch({ templateId: null });
      }

      // If it was a template but is now a note - find notes that referenced it and clear their templateId
      if (updatedRxDoc.variant === 'notes' && rxDoc.variant === 'templates') {
        await clearTemplateIdFromLinkedNotes(notesCollection, updatedRxDoc.id);
      }
    }

    onSuccess?.(updatedRxDoc);
    return updatedRxDoc;
  };

  return { mutate: patchNote, isLoading };
};

// delete

export const useNotesDeleteByIds = () => {
  const { mutate: deleteNotes, isLoading } = useRXDeleteByIds<Note>('notes');

  const deleteNotesAndCloseTheirTabs = async (ids: string[], mutationOptions: MutationOptions = {}) => {
    if (!ids.length) return;

    await deleteNotes(ids, mutationOptions);
  };

  return { mutate: deleteNotesAndCloseTheirTabs, isLoading };
};

// upsert

export const useNoteWriter = (
  noteRxDoc: RxDocument<Note> | undefined,
  noteId: string | undefined,
  { variant = 'notes', createIfMissing }: { variant?: NoteVariant; createIfMissing: boolean }
) =>
  useRxWriter(noteRxDoc, noteId, {
    collectionName: 'notes',
    create: useNoteCreate(variant),
    patch: useNotePatch(),
    createIfMissing,
  });
